import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {useDownloadZipFormas, useStartZipFormas, type BateriaEvaluacion} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'

/** El estado va junto al control que describe: `listo` viaja dentro de «Descargar ZIP». Siempre
 * `outline`: el único verde de la pantalla es la barra de estado del lote. */
export function ZipFormasControl({
  businessId,
  evaluacionId,
  evaluacion,
  selectedPaqueteIds,
  hasPaquetes,
}: {
  businessId: string
  evaluacionId: string
  evaluacion: BateriaEvaluacion
  selectedPaqueteIds: string[]
  hasPaquetes: boolean
}) {
  const {t} = useTranslation()
  const start = useStartZipFormas(businessId, evaluacionId)
  const download = useDownloadZipFormas(businessId, evaluacionId)
  const [error, setError] = useState<string | null>(null)
  const status = evaluacion.zip_formas_status
  const running = status === 'pendiente' || status === 'procesando'

  function generate() {
    setError(null)
    start.mutate(selectedPaqueteIds, {onError: (err) => setError(translateApiError(err))})
  }

  function downloadZip() {
    setError(null)
    download.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
  }

  const label =
    selectedPaqueteIds.length > 0
      ? t('admin.bateria.zip.generateSelection', {count: selectedPaqueteIds.length})
      : t('admin.bateria.zip.generateAll')

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        data-testid="generar-zip"
        disabled={!hasPaquetes || running || start.isPending}
        onClick={generate}>
        {label}
      </Button>
      {running && (
        <Tag tone="amber" data-testid="zip-status">
          {t(`admin.bateria.zip.status.${status}`)}
        </Tag>
      )}
      {status === 'listo' && (
        <Button
          size="sm"
          variant="outline"
          data-testid="descargar-zip"
          disabled={download.isPending}
          onClick={downloadZip}>
          {t('admin.bateria.zip.download')}
          <Tag tone="green" data-testid="zip-status">
            {t('admin.bateria.zip.status.listo')}
          </Tag>
        </Button>
      )}
      {/* El reintento funciona: `running` vuelve a false con `error` y el botón queda habilitado. */}
      {status === 'error' && (
        <>
          <Tag tone="red" data-testid="zip-status">
            {t('admin.bateria.zip.status.error')}
          </Tag>
          <span className="text-xs text-muted-foreground" data-testid="zip-error-salida">
            {t('admin.bateria.zip.status.errorSalida')}
          </span>
        </>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  )
}
